import './AddQuizDialog.scss';

import * as React from 'react';

import { translate } from '~l10n/client';

import { Badge } from '~shared/components/Badge';
import { Dialog, DialogTitle } from '~shared/components/Dialog';
import { TileButton } from '~shared/components/TileButton';

import {
  BlankQuizIcon,
  GenerateQuestionsIcon,
  ImportFromTextIcon,
  ImportQuizIcon,
} from './Icons';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  generateQuestions: () => void;
  importFromText: () => void;
  createBlank: () => void;
  importQuiz: () => void;
}

export function useAddQuizDialog() {
  const [isAddQuizDialogOpen, setIsAddQuizDialogOpen] = React.useState(false);

  const openAddQuizDialog = () => {
    setIsAddQuizDialogOpen(true);
  };
  const closeAddQuizDialog = () => setIsAddQuizDialogOpen(false);

  return { isAddQuizDialogOpen, openAddQuizDialog, closeAddQuizDialog };
}

export function AddQuizDialog({
  isOpen,
  onClose,
  importQuiz,
  createBlank,
  generateQuestions,
  importFromText,
}: Props) {
  return (
    <Dialog
      title={translate('Add Quiz')}
      onClose={onClose}
      open={isOpen}
      size="full"
      id="add-quiz"
    >
      {({ headingRef, title }) => {
        return (
          <div className="flex flex-col justify-center gap-4 add-quiz-dialog">
            <div
              ref={headingRef}
              id="add-quiz-title"
              className="flex justify-center font-display"
            >
              <DialogTitle weight="bold">{title}</DialogTitle>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <TileButton
                className="add-quiz-tile-button"
                icon={BlankQuizIcon}
                title={translate('Blank Quiz')}
                description={translate(
                  'Jump right in and build something great.'
                )}
                onClick={createBlank}
              />
              <TileButton
                className="add-quiz-tile-button"
                icon={GenerateQuestionsIcon}
                title={
                  <div className="flex items-center gap-2">
                    <p className="font-600">
                      {translate('Generate Questions')}
                    </p>
                    <Badge
                      textTransform="uppercase"
                      tone="beta"
                      className="w-fit"
                    >
                      {translate('Beta')}
                    </Badge>
                  </div>
                }
                description={translate(
                  'Save time by describing your quiz and AI will create your first draft.'
                )}
                onClick={generateQuestions}
              />
              <TileButton
                className="add-quiz-tile-button"
                icon={ImportFromTextIcon}
                title={
                  <div className="flex items-center gap-2">
                    <p className="font-600">
                      {translate('Copy-Paste Questions')}
                    </p>
                    <Badge
                      textTransform="uppercase"
                      tone="beta"
                      className="w-fit"
                    >
                      {translate('Beta')}
                    </Badge>
                  </div>
                }
                description={translate(
                  'Paste questions from another resource and convert them into a quiz.'
                )}
                onClick={importFromText}
              />
              <TileButton
                className="add-quiz-tile-button"
                icon={ImportQuizIcon}
                title={translate('Import Quiz')}
                description={translate(
                  'Import a quiz from another user or from an excel sheet.'
                )}
                onClick={importQuiz}
              />
            </div>
          </div>
        );
      }}
    </Dialog>
  );
}
